import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Calendar,
  Users,
  Award,
  Play,
  ChevronRight,
  GraduationCap,
  Clock,
  Star,
  TrendingUp,
  Check,
  Video,
  BarChart,
  Zap,
  X,
  ArrowRight
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [institutionName, setInstitutionName] = useState('NPBC Theological College');
  const [institutionColors, setInstitutionColors] = useState(['#667eea', '#764ba2']);
  const [landingConfig, setLandingConfig] = useState(null);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [quickShortcuts, setQuickShortcuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalContent, setModalContent] = useState(null);

  useEffect(() => {
    loadInstitutionData();
  }, []);

  const loadInstitutionData = async () => {
    try {
      const selectedInstitutionString = localStorage.getItem('selectedInstitution');
      if (selectedInstitutionString) {
        const selectedInstitution = JSON.parse(selectedInstitutionString);
        setInstitutionName(selectedInstitution.name || 'NPBC Theological College');

        // Set colors for gradient
        if (selectedInstitution.primaryColor && selectedInstitution.secondaryColor) {
          setInstitutionColors([
            selectedInstitution.primaryColor,
            selectedInstitution.secondaryColor
          ]);
        }

        // Fetch landing page configuration
        if (selectedInstitution.id) {
          await fetchLandingPageConfig(selectedInstitution.id);
        }
      }
    } catch (error) {
      console.error('Error loading institution data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingPageConfig = async (institutionId) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/landing-page/${institutionId}`);
      const data = await response.json();

      setLandingConfig(data.config);
      setFeaturedArticles(data.featuredArticles || []);
      setLatestNews(data.newsItems || []);
      setQuickShortcuts(data.quickShortcuts || []);
    } catch (error) {
      console.error('Error fetching landing page config:', error);
      // Use default/fallback data
      setDefaultData();
    }
  };

  const setDefaultData = () => {
    // Fallback to default data if API fails
    setFeaturedArticles([
      {
        id: 1,
        title: "The Power of Faith in Biblical Studies",
        excerpt: "Exploring how faith deepens our understanding of Scripture and transforms theological education...",
        category: "Faith & Learning",
        readTime: "5 min read",
        imageEmoji: "📖",
      },
      {
        id: 2,
        title: "Understanding Biblical Hermeneutics",
        excerpt: "A comprehensive guide to interpreting Scripture with historical and cultural context...",
        category: "Biblical Studies",
        readTime: "8 min read",
        imageEmoji: "🔍",
      },
      {
        id: 3,
        title: "Church Leadership in Modern Times",
        excerpt: "Balancing traditional biblical principles with contemporary leadership challenges...",
        category: "Leadership",
        readTime: "6 min read",
        imageEmoji: "👥",
      },
    ]);

    setQuickShortcuts([
      { id: 1, title: "Daily Devotions", icon: "🙏", actionType: "alert", actionValue: "Coming Soon: Daily devotions feature will be available soon." },
      { id: 2, title: "Sermon Library", icon: "🎙️", actionType: "alert", actionValue: "Coming Soon: Sermon library feature will be available soon." },
      { id: 3, title: "Prayer Requests", icon: "⛪", actionType: "alert", actionValue: "Coming Soon: Prayer requests feature will be available soon." },
      { id: 4, title: "Bible Study", icon: "📚", actionType: "navigate", actionValue: "/login" },
    ]);

    setLatestNews([
      {
        id: 1,
        title: "New Biblical Languages Course Launch",
        excerpt: "Exciting announcement: Hebrew and Greek language courses now available for all students seeking deeper scriptural understanding.",
        dateText: "3 days ago",
        category: "Academics",
      },
      {
        id: 2,
        title: "Campus Revival Week Highlights",
        excerpt: "Testimonies and insights from our recent campus-wide revival meetings that touched hearts across our community.",
        dateText: "1 week ago",
        category: "Campus Life",
      },
      {
        id: 3,
        title: "Theological Symposium 2025",
        excerpt: "Join renowned theologians and biblical scholars for our annual symposium on contemporary Christian thought.",
        dateText: "2 weeks ago",
        category: "Events",
      },
    ]);
  };

  const handleArticlePress = (article) => {
    if (article.articleUrl) {
      window.open(article.articleUrl, '_blank');
    } else {
      setModalContent({
        title: article.title,
        content: article.excerpt,
        type: 'article'
      });
    }
  };

  const handleNewsPress = (news) => {
    if (news.fullContentUrl) {
      window.open(news.fullContentUrl, '_blank');
    } else {
      setModalContent({
        title: news.title,
        content: news.excerpt,
        type: 'news'
      });
    }
  };

  const handleShortcutAction = (shortcut) => {
    if (shortcut.actionType === 'navigate') {
      navigate(shortcut.actionValue);
    } else if (shortcut.actionType === 'external_link') {
      window.open(shortcut.actionValue, '_blank');
    } else {
      setModalContent({
        title: shortcut.title,
        content: shortcut.actionValue || "This feature is currently under development.",
        type: 'shortcut'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Banner */}
      <div
        className="relative bg-gradient-to-r text-white py-12"
        style={{
          background: `linear-gradient(to right, ${institutionColors[0]}, ${institutionColors[1]})`
        }}
      >
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 p-4 rounded-full">
              <GraduationCap className="w-12 h-12" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Welcome to {institutionName}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-6">
            {landingConfig?.heroSubtitle || 'Faith • Learning • Community'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/login?role=student')}
              className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center"
            >
              Student Login
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
            <button
              onClick={() => navigate('/login?role=lecturer')}
              className="bg-white/20 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors border border-white/30"
            >
              Lecturer Login
            </button>
          </div>
        </div>
      </div>

      {/* Free Trial Banner - REMOVED */}

      {/* Features Grid */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Everything You Need to Excel
          </h2>
          <p className="text-lg text-gray-600">
            Comprehensive tools for students, lecturers, and administrators
          </p>
        </div>
      </div>

      {/* Featured Video Section */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-5 flex justify-between items-center text-white">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">🎬 Church Social</h2>
              <p className="text-blue-100 text-sm">Latest biblical insights and teachings</p>
            </div>
            <button className="text-white hover:text-blue-200 font-medium text-sm">
              View All
            </button>
          </div>
          <div className="p-6">
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <div className="aspect-video bg-gray-800 flex items-center justify-center relative">
                <Play className="w-16 h-16 text-white" />
                <div className="absolute bottom-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-sm">
                  {landingConfig?.featuredVideoDuration || '25:30'}
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-white mb-1">
                  {landingConfig?.featuredVideoTitle || 'Understanding Biblical Prophecy'}
                </h3>
                <p className="text-gray-300 text-sm">
                  {landingConfig?.featuredVideoAuthor || 'Dr. Sarah Mitchell'} • {landingConfig?.featuredVideoDepartment || 'Systematic Theology'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Shortcuts */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">⚡ Quick Shortcuts</h2>
          <p className="mt-2 text-sm md:text-base text-gray-500">Fast access to important resources</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {quickShortcuts.map((shortcut) => (
            <button
              key={shortcut.id}
              onClick={() => handleShortcutAction(shortcut)}
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow text-center"
            >
              <div className="text-3xl mb-3">
                {shortcut.icon === 'Book' ? '📚' : (shortcut.icon || '📚')}
              </div>
              <h3 className="font-semibold text-gray-900">{shortcut.title}</h3>
            </button>
          ))}
        </div>
      </div>

      {/* Featured Articles Slider */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">📚 Featured Articles</h2>
            <p className="mt-2 text-sm md:text-base text-gray-500">Latest insights and tutorials from our community</p>
          </div>
          <button onClick={() => window.location.href = '/articles'} className="text-blue-600 font-semibold hover:text-blue-700 transition-colors flex items-center gap-1">
            View All <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featuredArticles.map((article) => (
            <div
              key={article.id}
              onClick={() => handleArticlePress(article)}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer overflow-hidden"
            >
              <div className="h-48 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                <span className="text-6xl">
                  {article.imageEmoji === 'Book' ? '📖' : (article.imageEmoji || article.image || '📖')}
                </span>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                    {article.category}
                  </span>
                  <span className="text-gray-500 text-sm">{article.readTime}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-3">
                  {article.excerpt}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Verse of the Day */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 text-center border border-blue-100">
          <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-2">✨ Verse of the Day</h2>
          <blockquote className="text-lg text-gray-700 italic mb-4">
            {landingConfig?.verseText || '"For I know the plans I have for you," declares the Lord, "plans to prosper you and not to harm you, to give you hope and a future."'}
          </blockquote>
          <cite className="text-blue-600 font-semibold">— {landingConfig?.verseReference || 'Jeremiah 29:11 (NIV)'}</cite>
        </div>
      </div>

      {/* Latest News */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">📰 Latest News</h2>
          <button className="text-blue-600 hover:text-blue-700 font-medium">
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latestNews.map((news) => (
            <div
              key={news.id}
              onClick={() => handleNewsPress(news)}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer p-6"
            >
              <div className="flex justify-between items-start mb-3">
                <span className={`text-xs font-medium px-2 py-1 rounded ${news.category === 'Academics' ? 'bg-green-100 text-green-800' :
                  news.category === 'Campus Life' ? 'bg-blue-100 text-blue-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                  {news.category}
                </span>
                <span className="text-gray-500 text-sm">{news.dateText || news.date}</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {news.title}
              </h3>
              <p className="text-gray-600 text-sm line-clamp-3">
                {news.excerpt}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Community Stats */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-6 flex items-center justify-center gap-2">📊 Our Community</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">{landingConfig?.studentsCount || '850+'}</div>
            <div className="text-gray-600">Students</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">{landingConfig?.facultyCount || '45'}</div>
            <div className="text-gray-600">Faculty</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">{landingConfig?.coursesCount || '25+'}</div>
            <div className="text-gray-600">Courses</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">{landingConfig?.campusesCount || '12'}</div>
            <div className="text-gray-600">Campuses</div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {landingConfig?.ctaTitle || 'Begin Your Spiritual Journey'}
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            {landingConfig?.ctaSubtitle || 'Join our community of learners growing in faith and knowledge'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center"
          >
            {landingConfig?.ctaButtonText || '🎓 Go to Dashboard'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
      {/* Dynamic Modal */}
      {modalContent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-fade-in-up">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                {modalContent.title}
              </h3>
              <button
                onClick={() => setModalContent(null)}
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="prose max-w-none text-gray-600">
                <p>{modalContent.content}</p>
              </div>

              {modalContent.type === 'article' || modalContent.type === 'news' ? (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setModalContent(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
                  <p>🔔 This feature is coming soon to the platform.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default Landing;
